/**
 * High-level convenience class that wraps the full ARBI stack into a simple API.
 *
 * Handles SDK client creation, sodium initialization, authentication,
 * workspace key decryption, and delegates to all operations modules.
 *
 * Usage:
 *   const arbi = new Arbi({ url: 'https://arbi.mycompany.com' })
 *   await arbi.login('user@example.com', 'password')
 *   const workspaces = await arbi.workspaces.list()
 *   await arbi.selectWorkspace(workspaces[0].external_id)
 *   const docs = await arbi.documents.list()
 */

import {
  createArbiClient,
  type ArbiClient,
  type LoginResult,
  type components,
} from '@arbidocs/client'

type DocUpdateRequest = components['schemas']['DocUpdateRequest']
type WorkspaceUpdateRequest = components['schemas']['WorkspaceUpdateRequest']
type UpdateTagRequest = components['schemas']['UpdateTagRequest']
type TagFormat = components['schemas']['TagFormat']
// Doctag notes are grounded by a flat passage list; CitationData (per-statement,
// with offsets) remains the shape for assistant-message citations.
type CitationSources = components['schemas']['CitationSources']
type UserSettingsUpdate = components['schemas']['UserSettingsUpdate']
type ConfigUpdateData = components['schemas']['ConfigUpdateData']
import { ArbiError } from './errors.js'
import { selectWorkspace as selectWorkspaceInternal } from './auth.js'
import { streamSSE, type SSEStreamCallbacks, type SSEStreamResult } from './sse.js'
import type {
  SSEStreamStartData,
  AgentStepEvent,
  UserMessageEvent,
  MessageMetadataPayload,
  UserInputRequestEvent,
  ArtifactEvent,
} from './sse-types.js'
import * as documentsOps from './operations/documents.js'
import type { ListPaginatedOptions, ListAllOptions } from './operations/documents.js'
import * as workspacesOps from './operations/workspaces.js'
import * as conversationsOps from './operations/conversations.js'
import * as assistantOps from './operations/assistant.js'
import * as tagsOps from './operations/tags.js'
import * as contactsOps from './operations/contacts.js'
import * as doctagsOps from './operations/doctags.js'
import * as dmOps from './operations/dm.js'
import type { DmCryptoContext } from './operations/dm.js'
import * as settingsOps from './operations/settings.js'
import * as agentconfigOps from './operations/agentconfig.js'
import * as healthOps from './operations/health.js'
import * as filesOps from './operations/files.js'
import * as agentsOps from './operations/agents.js'
import * as sessionsOps from './operations/sessions.js'
import * as projectsOps from './operations/projects.js'
import * as tasksOps from './operations/tasks.js'
import * as factOps from './operations/facts.js'

// ── Options ─────────────────────────────────────────────────────────────────

export interface ArbiOptions {
  /** Backend API URL (e.g. 'https://arbi.mycompany.com') */
  url: string
  /** Deployment domain for key derivation. Defaults to hostname of url. */
  deploymentDomain?: string
  /** Include credentials (cookies) in requests. Default: 'omit' for SDK consumers. */
  credentials?: 'include' | 'omit' | 'same-origin'
}

// ── Assistant query options ─────────────────────────────────────────────────

export interface QueryOptions {
  /** Document IDs to search against */
  docIds: string[]
  /** Previous response ID for follow-up questions */
  previousResponseId?: string | null
  /** Model to use for generation */
  model?: string
  /** Called for each streaming token */
  onToken?: (content: string) => void
  /** Called when stream starts */
  onStreamStart?: (data: SSEStreamStartData) => void
  /** Called for each agent step */
  onOutputItemDone?: (data: import('./sse-types.js').ResponseOutputItemDoneEvent) => void
  /** Called on stream error */
  onError?: (message: string) => void
  /** Called when a user message event is received */
  onUserMessage?: (data: UserMessageEvent) => void
  /** Called when message metadata is received */
  onMetadata?: (data: MessageMetadataPayload) => void
  /** Called when the agent requests user input */
  onUserInputRequest?: (data: UserInputRequestEvent) => void
  /** Called when an artifact event is received */
  onArtifact?: (data: ArtifactEvent) => void
  /** Called when backend elapsed time is received */
  onElapsedTime?: (t: number) => void
  /** Called when stream completes */
  onComplete?: () => void
}

// ── Arbi class ──────────────────────────────────────────────────────────────

export class Arbi {
  private client: ArbiClient | null = null
  private loginResult: LoginResult | null = null
  private currentWorkspaceId: string | null = null
  private dmCryptoContext: DmCryptoContext | null = null
  private readonly options: ArbiOptions

  constructor(options: ArbiOptions) {
    this.options = options
  }

  // ── Lifecycle ───────────────────────────────────────────────────────────

  /** Initialize the SDK client and sodium crypto. Called automatically by login(). */
  async init(): Promise<ArbiClient> {
    if (this.client) return this.client

    const domain = this.options.deploymentDomain ?? new URL(this.options.url).hostname

    this.client = createArbiClient({
      baseUrl: this.options.url,
      deploymentDomain: domain,
      credentials: this.options.credentials ?? 'omit',
    })

    await this.client.crypto.initSodium()
    return this.client
  }

  /**
   * Request a verification email for registration.
   * The user will receive an email with a 3-word verification code.
   */
  async requestVerification(email: string): Promise<void> {
    const client = await this.init()
    await client.fetch.POST('/v1/user/verify-email', { body: { email } })
  }

  /**
   * Register a new account using the verification code received by email.
   */
  async register(params: {
    email: string
    password: string
    verificationCode: string
    firstName?: string
    lastName?: string
  }): Promise<void> {
    const client = await this.init()
    await client.auth.register(params)
  }

  /**
   * Log in with email and password.
   * Initializes the SDK client if not already done.
   */
  async login(email: string, password: string): Promise<void> {
    const client = await this.init()
    this.loginResult = await client.auth.login({ email, password })
  }

  /**
   * Recover a session using a stored signing private key (base64).
   * Useful for session recovery without re-entering the password.
   */
  async loginWithKey(email: string, signingPrivateKeyBase64: string): Promise<void> {
    const client = await this.init()
    const signingPrivateKey = client.crypto.base64ToBytes(signingPrivateKeyBase64)
    this.loginResult = await client.auth.loginWithKey({ email, signingPrivateKey })
  }

  /**
   * Select a workspace by ID. Fetches the workspace list, finds the matching
   * workspace, decrypts the wrapped key, and sets up auth headers.
   */
  async selectWorkspace(workspaceId: string): Promise<void> {
    const client = this.requireClient()
    const lr = this.requireLogin()

    const allWorkspaces = await workspacesOps.listWorkspaces(client)
    const ws = allWorkspaces.find((w) => w.external_id === workspaceId)
    if (!ws || !ws.wrapped_key) {
      throw new ArbiError(`Workspace ${workspaceId} not found or has no encryption key`)
    }

    const signingPrivateKeyBase64 = client.crypto.bytesToBase64(lr.signingPrivateKey)
    const sealedKey = await selectWorkspaceInternal(
      client,
      ws.external_id,
      ws.wrapped_key,
      lr.serverSessionKey,
      signingPrivateKeyBase64
    )

    // Open the workspace (stores encrypted key in session, sets active)
    await client.fetch.POST('/v1/workspace/{workspace_ext_id}/open', {
      params: { path: { workspace_ext_id: ws.external_id } },
      body: { workspace_key: sealedKey },
    })

    this.currentWorkspaceId = ws.external_id
  }

  /** Log out and clear internal state. */
  async logout(): Promise<void> {
    if (this.client) {
      await this.client.auth.logout()
    }
    if (this.loginResult) {
      this.loginResult.signingPrivateKey.fill(0)
      this.loginResult.serverSessionKey.fill(0)
    }
    this.loginResult = null
    this.currentWorkspaceId = null
    this.dmCryptoContext = null
  }

  // ── Accessors ─────────────────────────────────────────────────────────

  /** Get the underlying ArbiClient (throws if not initialized). */
  getClient(): ArbiClient {
    return this.requireClient()
  }

  /** Get the current workspace ID (throws if none selected). */
  getWorkspaceId(): string {
    return this.requireWorkspace()
  }

  /** Check if the user is logged in. */
  get isLoggedIn(): boolean {
    return this.loginResult !== null
  }

  /** Check if a workspace is selected. */
  get hasWorkspace(): boolean {
    return this.currentWorkspaceId !== null
  }

  /**
   * Live connection info for opening a raw transport (e.g. a WebSocket).
   *
   * Reads the access token from the session on each call, so a token refreshed
   * elsewhere is reflected on the next (re)connect. Returns `null` when there is
   * no client/token yet (not logged in) rather than throwing, so callers can
   * poll for readiness instead of guarding against exceptions.
   */
  getConnectionInfo(): { baseUrl: string; accessToken: string } | null {
    const accessToken = this.client?.session.getState().accessToken
    if (!accessToken) return null
    return { baseUrl: this.options.url, accessToken }
  }

  // ── Auth headers (for raw fetch operations) ───────────────────────────

  private getAuthHeaders() {
    const client = this.requireClient()
    const accessToken = client.session.getState().accessToken

    if (!accessToken) {
      throw new ArbiError('Missing access token')
    }

    return {
      baseUrl: this.options.url,
      accessToken,
    }
  }

  // ── Projects ──────────────────────────────────────────────────────────

  readonly projects = {
    list: () => projectsOps.listProjects(this.requireClient()),
    create: (name: string) => projectsOps.createProject(this.requireClient(), name),
    refresh: (projectExtId: string) =>
      projectsOps.refreshProject(this.requireClient(), projectExtId),
    /** AI usage + spend breakdown for a project's billing period. */
    usage: (projectExtId: string, monthsBack?: number) =>
      projectsOps.getProjectUsage(this.requireClient(), projectExtId, monthsBack),
    /** Stripe invoices for a project's subscription. */
    invoices: (projectExtId: string) =>
      projectsOps.getProjectInvoices(this.requireClient(), projectExtId),
  }

  // ── User (account-scoped usage) ───────────────────────────────────────

  readonly user = {
    /**
     * The authenticated user's identity from the current session
     * (`external_id`, plus `email` when the session carries one), established at
     * login. Returns `null` when not logged in. This is the canonical "who am I"
     * accessor — DM direction and ownership checks read it instead of re-deriving
     * identity per call.
     *
     * The external_id alone decides identity. A session without an email (e.g. an
     * agent, or a key-restored session) is still a known user, so `email` is
     * nullable rather than gating the whole identity: an ownership check must
     * never silently fail open just because the email is missing.
     */
    identity: (): { externalId: string; email: string | null } | null => {
      const state = this.requireClient().session.getState()
      return state.userExtId
        ? { externalId: state.userExtId, email: state.userEmail ?? null }
        : null
    },
    /** Today's deployment-wide credit spend for the current user. */
    usageToday: () => projectsOps.getUserUsageToday(this.requireClient()),
  }

  // ── Workspaces ────────────────────────────────────────────────────────

  readonly workspaces = {
    list: () => workspacesOps.listWorkspaces(this.requireClient()),

    create: async (
      name: string,
      projectExtId: string,
      description?: string | null,
      isPublic?: boolean
    ) => {
      const client = this.requireClient()
      const lr = this.requireLogin()
      await client.crypto.initSodium()
      const sodium = (await import('libsodium-wrappers-sumo')).default
      await sodium.ready
      const workspaceKey = sodium.randombytes_buf(32)
      const encryptedKey = await client.crypto.sealKeyForSession(workspaceKey, lr.serverSessionKey)
      return workspacesOps.createWorkspace(
        client,
        name,
        encryptedKey,
        projectExtId,
        description,
        isPublic
      )
    },

    delete: (workspaceIds: string[]) =>
      workspacesOps.deleteWorkspaces(this.requireClient(), workspaceIds),

    update: (body: WorkspaceUpdateRequest) =>
      workspacesOps.updateWorkspace(this.requireClient(), body),

    listUsers: () => workspacesOps.listWorkspaceUsers(this.requireClient()),

    addUsers: (emails: string[], role?: 'owner' | 'collaborator' | 'guest') =>
      workspacesOps.addWorkspaceUsers(this.requireClient(), emails, role),

    removeUsers: (userIds: string[]) =>
      workspacesOps.removeWorkspaceUsers(this.requireClient(), userIds),

    setUserRole: (userIds: string[], role: 'owner' | 'collaborator' | 'guest') =>
      workspacesOps.setUserRole(this.requireClient(), userIds, role),

    /**
     * Ask another user to grant you workspace access. Builds the E2E-encrypted
     * request `content` (carrying this session's public key for a temporary
     * grant) and posts it. `workspaceExtId` is optional — pass it only when you
     * already know the target workspace id (obtained out-of-band).
     */
    requestAccess: (params: {
      recipientExtId: string
      recipientEncryptionPublicKey: string
      note?: string
      workspaceExtId?: string
    }) => {
      const client = this.requireClient()
      const lr = this.requireLogin()
      const userExtId = lr.userExtId ?? client.session.getState().userExtId
      if (!userExtId) throw new ArbiError('No user id in session — cannot request access')
      const crypto = dmOps.createDmCryptoContext(client, lr.signingPrivateKey, userExtId)
      const sessionPubkeyB64 = client.crypto.bytesToBase64(lr.serverSessionKey)
      return dmOps.requestWorkspaceAccess(client, { ...params, sessionPubkeyB64 }, crypto)
    },

    copyDocuments: (targetWorkspaceId: string, docIds: string[], targetWorkspaceKey: string) =>
      workspacesOps.copyDocuments(
        this.requireClient(),
        targetWorkspaceId,
        docIds,
        targetWorkspaceKey
      ),
  }

  // ── Documents ─────────────────────────────────────────────────────────

  readonly documents = {
    list: () => documentsOps.listDocuments(this.requireClient()),

    /**
     * Async iterator that yields pages of documents sequentially.
     * Use this for large workspaces to stream output as pages arrive.
     *
     * @example
     * ```ts
     * for await (const page of arbi.documents.listPaginated({ pageSize: 5000, order: 'created_desc', fields: 'lite' })) {
     *   // page: DocResponse[] — render incrementally
     * }
     * ```
     */
    listPaginated: (options?: ListPaginatedOptions) =>
      documentsOps.listPaginated(this.requireClient(), options),

    /**
     * Collect all workspace documents into a single array using sequential pagination.
     *
     * **Note:** When `fields: 'lite'` is passed, returned docs have `doctags: []`
     * and `doc_metadata: null`. Fetch full per-doc data on demand when a specific
     * doc is opened.
     */
    listAll: (options?: ListAllOptions) => documentsOps.listAll(this.requireClient(), options),

    get: (externalIds: string[]) => documentsOps.getDocuments(this.requireClient(), externalIds),

    delete: (externalIds: string[]) =>
      documentsOps.deleteDocuments(this.requireClient(), externalIds),

    update: (documents: DocUpdateRequest[]) =>
      documentsOps.updateDocuments(this.requireClient(), documents),

    uploadUrl: (urls: string[], shared?: boolean) => {
      this.requireWorkspace()
      return documentsOps.uploadUrl(this.requireClient(), urls, shared)
    },

    uploadFile: (fileData: Blob, fileName: string, options?: { folder?: string }) => {
      this.requireWorkspace()
      return documentsOps.uploadFile(
        this.getAuthHeaders(),
        fileData,
        fileName,
        options?.folder ? { folder: options.folder } : undefined
      )
    },

    download: (docId: string) => documentsOps.downloadDocument(this.getAuthHeaders(), docId),

    getParsedContent: (docId: string, stage: string) =>
      documentsOps.getParsedContent(this.getAuthHeaders(), docId, stage),
    getThumbnails: (docIds: string[]) => documentsOps.getThumbnails(this.getAuthHeaders(), docIds),
  }

  // ── Conversations ─────────────────────────────────────────────────────

  readonly conversations = {
    list: () => conversationsOps.listConversations(this.requireClient()),

    getThreads: (conversationId: string) =>
      conversationsOps.getConversationThreads(this.requireClient(), conversationId),

    delete: (conversationId: string) =>
      conversationsOps.deleteConversation(this.requireClient(), conversationId),

    share: (conversationId: string) =>
      conversationsOps.shareConversation(this.requireClient(), conversationId),

    updateTitle: (conversationId: string, title: string) =>
      conversationsOps.updateConversationTitle(this.requireClient(), conversationId, title),

    getMessage: (messageId: string) => conversationsOps.getMessage(this.requireClient(), messageId),

    deleteMessage: (messageId: string) =>
      conversationsOps.deleteMessage(this.requireClient(), messageId),
  }

  // ── Assistant ─────────────────────────────────────────────────────────

  readonly assistant = {
    /**
     * Send a question to the RAG assistant with streaming support.
     * Returns the accumulated result after the stream completes.
     */
    query: async (question: string, options: QueryOptions): Promise<SSEStreamResult> => {
      const auth = this.getAuthHeaders()
      const workspaceId = this.requireWorkspace()

      const response = await assistantOps.queryAssistant({
        ...auth,
        workspaceId,
        question,
        docIds: options.docIds,
        previousResponseId: options.previousResponseId,
        model: options.model,
      })

      const callbacks: SSEStreamCallbacks = {
        onToken: options.onToken,
        onStreamStart: options.onStreamStart,
        onOutputItemDone: options.onOutputItemDone,
        onError: options.onError,
        onUserMessage: options.onUserMessage,
        onMetadata: options.onMetadata,
        onUserInputRequest: options.onUserInputRequest,
        onArtifact: options.onArtifact,
        onElapsedTime: options.onElapsedTime,
        onComplete: options.onComplete,
      }

      return streamSSE(response, callbacks)
    },

    /**
     * Respond to an agent's question during a human-in-the-loop workflow.
     */
    respond: (assistantMessageExtId: string, answer: string) =>
      assistantOps.respondToAgent(this.requireClient(), assistantMessageExtId, answer),

    /**
     * Search documents without LLM generation (retrieval only).
     */
    retrieve: (
      query: string,
      docIds: string[],
      options?: {
        searchMode?: 'semantic' | 'keyword' | 'hybrid'
        fullContextDocIds?: string[]
        tocDocIds?: string[]
        model?: string
      }
    ) =>
      assistantOps.retrieve({
        arbi: this.requireClient(),
        workspaceId: this.requireWorkspace(),
        query,
        docIds,
        ...options,
      }),
  }

  // ── Tags ──────────────────────────────────────────────────────────────

  readonly tags = {
    list: () => tagsOps.listTags(this.requireClient()),

    create: (options: {
      name: string
      workspaceId?: string
      tagType?: TagFormat
      instruction?: string | null
      shared?: boolean
    }) =>
      tagsOps.createTag(this.requireClient(), {
        ...options,
        workspaceId: options.workspaceId ?? this.requireWorkspace(),
      }),

    delete: (tagId: string) => tagsOps.deleteTag(this.requireClient(), tagId),

    update: (tagId: string, body: UpdateTagRequest) =>
      tagsOps.updateTag(this.requireClient(), tagId, body),
  }

  // ── Document Tags ─────────────────────────────────────────────────────

  readonly doctags = {
    assign: (tagId: string, docIds: string[], note?: string | null) =>
      doctagsOps.assignDocTags(this.requireClient(), tagId, docIds, note),

    remove: (tagId: string, docIds: string[]) =>
      doctagsOps.removeDocTags(this.requireClient(), tagId, docIds),

    update: (
      tagId: string,
      docId: string,
      updates: { note?: string | null; citations?: CitationSources | null }
    ) => doctagsOps.updateDocTag(this.requireClient(), tagId, docId, updates),

    generate: (tagIds: string[], docIds: string[]) =>
      doctagsOps.generateDocTags(this.requireClient(), tagIds, docIds),
  }

  // ── Contacts ──────────────────────────────────────────────────────────

  readonly contacts = {
    list: () => contactsOps.listContacts(this.requireClient()),
    add: (emails: string[]) => contactsOps.addContacts(this.requireClient(), emails),
    remove: (contactIds: string[]) => contactsOps.removeContacts(this.requireClient(), contactIds),
    groupByStatus: contactsOps.groupContactsByStatus,
  }

  // ── Direct Messages ───────────────────────────────────────────────────

  /**
   * Build (and cache) the DM crypto context for the current session.
   *
   * Derives the user's curve25519 keypair from their signing key and pairs it
   * with their external_id — everything `list()`/`send()` need to encrypt and
   * decrypt. Cached for the session and cleared on logout. Consumers never touch
   * this: it's an internal of the `dm` surface.
   *
   * The key comes from the live login result first, exactly as `selectWorkspace`
   * sources it, and only then from persisted storage (which is what restores a
   * session on reload). Reading storage first would make DMs the one feature that
   * breaks whenever persistence is unavailable — `saveSession` deliberately
   * no-ops without Web Crypto (a non-secure origin, tracking prevention, an
   * embedded widget), so a perfectly authenticated user with a working workspace
   * would be told they are "not authenticated" the moment they opened Messages.
   */
  private async requireDmCrypto(): Promise<DmCryptoContext> {
    if (this.dmCryptoContext) return this.dmCryptoContext
    const client = this.requireClient()
    const signingPrivateKey =
      this.loginResult?.signingPrivateKey ?? (await client.storage.getSession())?.signingPrivateKey
    if (!signingPrivateKey) {
      throw new ArbiError('Not authenticated — no session available for DM encryption')
    }
    const userExtId = client.session.getState().userExtId
    if (!userExtId) {
      throw new ArbiError('No user identity in session — cannot set up DM encryption')
    }
    this.dmCryptoContext = dmOps.createDmCryptoContext(client, signingPrivateKey, userExtId)
    return this.dmCryptoContext
  }

  readonly dm = {
    /**
     * List direct messages, newest first, with content already **decrypted**.
     * (A message that can't be decrypted comes back as `[Decryption failed]`.)
     */
    list: async () => dmOps.listDecryptedDMs(this.requireClient(), await this.requireDmCrypto()),

    /**
     * Send an E2E-encrypted DM to a recipient (email or `usr-`/`agt-` id).
     * Resolves the recipient's public key and encrypts internally — the caller
     * passes plaintext and never handles keys or ciphertext.
     */
    send: async (recipient: string, text: string) => {
      const client = this.requireClient()
      const resolved = await dmOps.resolveRecipient(client, recipient)
      if (!resolved) {
        throw new ArbiError(
          `Could not resolve DM recipient "${recipient}" (not a known contact, agent, or workspace member)`
        )
      }
      return dmOps.sendEncryptedDM(
        client,
        [
          {
            recipient_ext_id: resolved.extId,
            content: text,
            recipient_encryption_public_key: resolved.pubKey,
          },
        ],
        await this.requireDmCrypto()
      )
    },

    /**
     * Send to a recipient whose external_id + public key are already known
     * (e.g. from a message's `sender`), skipping directory resolution.
     */
    sendTo: async (recipientExtId: string, recipientPublicKey: string, text: string) =>
      dmOps.sendEncryptedDM(
        this.requireClient(),
        [
          {
            recipient_ext_id: recipientExtId,
            content: text,
            recipient_encryption_public_key: recipientPublicKey,
          },
        ],
        await this.requireDmCrypto()
      ),

    /** Resolve a recipient reference to `{ extId, pubKey }` (contacts → agents → workspace). */
    resolveRecipient: (recipient: string) =>
      dmOps.resolveRecipient(this.requireClient(), recipient),

    markRead: (messageIds: string[]) => dmOps.markRead(this.requireClient(), messageIds),
    delete: (messageIds: string[]) => dmOps.deleteDMs(this.requireClient(), messageIds),

    /** List the current user's durable activity feed (never unread; excluded from DMs). */
    listActivity: (options?: { limit?: number; offset?: number }) =>
      dmOps.listActivity(this.requireClient(), options),
  }

  // ── Settings ──────────────────────────────────────────────────────────

  readonly settings = {
    get: () => settingsOps.getSettings(this.requireClient()),
    update: (body: UserSettingsUpdate) => settingsOps.updateSettings(this.requireClient(), body),
  }

  // ── Agent Configuration ───────────────────────────────────────────────

  readonly agentConfig = {
    list: () => agentconfigOps.listConfigs(this.requireClient()),
    get: (configId: string) => agentconfigOps.getConfig(this.requireClient(), configId),
    save: (body: ConfigUpdateData) => agentconfigOps.saveConfig(this.requireClient(), body),
    delete: (configId: string) => agentconfigOps.deleteConfig(this.requireClient(), configId),
    getSchema: () => agentconfigOps.getSchema(this.requireClient()),
    getModels: () => agentconfigOps.getModels(this.requireClient()),
  }

  // ── Health ────────────────────────────────────────────────────────────

  readonly health = {
    check: () => healthOps.getHealth(this.requireClient()),
    models: () => healthOps.getHealthModels(this.requireClient()),
    remoteModels: () => healthOps.getRemoteModels(this.requireClient()),
    mcpTools: () => healthOps.getMcpTools(this.requireClient()),
  }

  // ── Files (OpenAI-compatible) ────────────────────────────────────────

  readonly files = {
    list: (options?: filesOps.ListFilesOptions) =>
      filesOps.listFiles(this.requireClient(), options),

    get: (fileId: string) => filesOps.getFile(this.requireClient(), fileId),

    delete: (fileId: string) => filesOps.deleteFile(this.requireClient(), fileId),

    upload: (fileData: Blob, fileName: string, purpose?: string) =>
      filesOps.uploadFile(this.getAuthHeaders(), fileData, fileName, purpose),

    getContent: (fileId: string) => filesOps.getFileContent(this.getAuthHeaders(), fileId),
  }

  // ── Agents ──────────────────────────────────────────────────────────

  readonly agents = {
    list: () => agentsOps.listAgents(this.requireClient()),
    create: (name: string) => agentsOps.createAgent(this.requireClient(), name),
    getConfig: (agentExtId: string) => agentsOps.getAgentConfig(this.requireClient(), agentExtId),
    listWorkspaces: (agentExtId: string) =>
      agentsOps.listAgentWorkspaces(this.requireClient(), agentExtId),
    delete: (agentExtIds: string[]) => agentsOps.deleteAgents(this.requireClient(), agentExtIds),
  }

  // ── Scheduled tasks ───────────────────────────────────────────────────
  readonly tasks = {
    list: () => tasksOps.listTasks(this.requireClient()),
    create: (body: tasksOps.CreateTaskRequest) => tasksOps.createTask(this.requireClient(), body),
    get: (taskId: string) => tasksOps.getTask(this.requireClient(), taskId),
    update: (taskId: string, body: tasksOps.UpdateTaskRequest) =>
      tasksOps.updateTask(this.requireClient(), taskId, body),
    delete: (taskId: string) => tasksOps.deleteTask(this.requireClient(), taskId),
    run: (taskId: string) => tasksOps.runTask(this.requireClient(), taskId),
  }

  // ── Workspace facts ─────────────────────────────────────────────────
  readonly facts = {
    create: (body: factOps.CreateFactRequest) => factOps.createFact(this.requireClient(), body),
    /** Pass `from`/`to`/`datedOnly` for the timeline view — dated notes only,
     *  ordered by when they happened. */
    list: (options?: { pinnedOnly?: boolean; from?: string; to?: string; datedOnly?: boolean }) =>
      factOps.listFacts(this.requireClient(), options),
    search: (body: factOps.FactSearchRequest) => factOps.searchFacts(this.requireClient(), body),
    update: (factId: string, body: factOps.UpdateFactRequest) =>
      factOps.updateFact(this.requireClient(), factId, body),
    delete: (factId: string) => factOps.deleteFact(this.requireClient(), factId),

    /** One-off reminder on a dated note: an explicit `remindAt`, or
     *  `leadMinutes` before its date. */
    remind: (factId: string, options: { remindAt?: string; leadMinutes?: number } = {}) =>
      factOps.remindFact(this.requireClient(), factId, options),
  }

  // ── Sessions ────────────────────────────────────────────────────────

  readonly sessions = {
    list: () => sessionsOps.listSessions(this.requireClient()),
  }

  // ── Internal guards ───────────────────────────────────────────────────

  private requireClient(): ArbiClient {
    if (!this.client) {
      throw new ArbiError('Not initialized. Call login() or init() first.')
    }
    return this.client
  }

  private requireLogin(): LoginResult {
    if (!this.loginResult) {
      throw new ArbiError('Not logged in. Call login() first.')
    }
    return this.loginResult
  }

  private requireWorkspace(): string {
    if (!this.currentWorkspaceId) {
      throw new ArbiError('No workspace selected. Call selectWorkspace() first.')
    }
    return this.currentWorkspaceId
  }
}
